/**
 * Cluster configuration and behavior definitions.
 * This file centralizes cluster-specific logic to make it easy to add new prod-like clusters.
 */

export interface ClusterBehavior {
  /** Cluster name/identifier */
  name: string;
  /** Whether this cluster skips team quota checks */
  skipTeamQuota: boolean;
  /** Whether this cluster should never force required dependencies */
  neverForceDependencies: boolean;
  /** Whether persistent environments are allowed */
  allowsPersistentEnvs: boolean;
}

/**
 * Production-like clusters that skip quota checks and don't force dependencies.
 * Add 'prod' to this list when ready to release to production.
 */
export const PROD_LIKE_CLUSTERS: string[] = ['preprod', 'sales'];

/**
 * Development clusters with special restrictions.
 */
export const DEV_CLUSTERS: string[] = ['dev'];

/**
 * QA clusters with special restrictions.
 */
export const QA_CLUSTERS: string[] = ['qa'];

/**
 * Team-restricted users (QA / Developers groups, flag-gated server-side) may only use their team's
 * cluster(s). The list is cached from /api/quickdeploy/authorized; absent means unrestricted.
 */
export function getAllowedClusters(): string[] | null {
  try {
    const raw = localStorage.getItem('allowed_clusters');
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function isClusterSelectable(clusterName: string): boolean {
  const allowed = getAllowedClusters();
  return allowed === null || allowed.includes(clusterName);
}

export const RESTRICTED_CLUSTER_REASON = "Clusters are assigned by team. Please select your team's cluster.";

/**
 * Get the currently selected cluster name.
 */
export function getCurrentCluster(): string {
  return (globalThis.cluster_selected_value as string) || '';
}

/**
 * Check if current cluster is production-like (skips quota, no forced deps).
 */
export function isProdLikeCluster(clusterName?: string): boolean {
  const cluster = clusterName ?? getCurrentCluster();
  return PROD_LIKE_CLUSTERS.includes(cluster);
}

/**
 * Check if current cluster is a dev cluster.
 */
export function isDevCluster(clusterName?: string): boolean {
  const cluster = clusterName ?? getCurrentCluster();
  return DEV_CLUSTERS.includes(cluster);
}

/**
 * Check if current cluster is a QA cluster.
 */
export function isQACluster(clusterName?: string): boolean {
  const cluster = clusterName ?? getCurrentCluster();
  return QA_CLUSTERS.includes(cluster);
}

/**
 * Check if team quota should be enforced on the current cluster.
 */
export function shouldEnforceTeamQuota(clusterName?: string): boolean {
  return !isProdLikeCluster(clusterName);
}

/**
 * Check if dependencies should be auto-forced on the current cluster.
 *
 * Prod-like clusters (preprod, sales, prod) use cloud-managed services (DBs etc.)
 * rather than shared infra, so required deps must never be auto-selected there.
 *
 * On non-prod clusters deps are always forced for non-deployed services.
 * The `deployedSet` guard inside markRequiredDependencies already ensures
 * already-running services are skipped, so no extra `skipQuota` check is needed here.
 *
 * @param skipQuota - unused here; kept for call-site compatibility
 */
export function shouldForceDependencies(skipQuota: boolean, clusterName?: string): boolean {
  const cluster = clusterName ?? getCurrentCluster();
  // Prod-like clusters never auto-select/force dependencies
  if (isProdLikeCluster(cluster)) {
    return false;
  }
  return true;
}

/**
 * Check if persistent environments are allowed on the current cluster.
 */
export function allowsPersistentEnvs(clusterName?: string): boolean {
  return !isDevCluster(clusterName);
}

/**
 * Get cluster behavior configuration.
 */
export function getClusterBehavior(clusterName?: string): ClusterBehavior {
  const cluster = clusterName ?? getCurrentCluster();
  
  return {
    name: cluster,
    skipTeamQuota: isProdLikeCluster(cluster),
    neverForceDependencies: isProdLikeCluster(cluster),
    allowsPersistentEnvs: !isDevCluster(cluster),
  };
}
